import { useQuery } from '@tanstack/react-query';

import { fetchReferencePlanDays, fetchStrategyPlan } from '../api/strategy';
import type { ReferencePlanDays, StrategyPlan } from '../types';
import { referenceKeys, strategyKeys } from './queryKeys';

/**
 * How often the plan is read while a generation runs. Writing a plan takes 30 to 180 seconds on the agent, so the
 * screen reads until the status leaves `generating`, as the previous screen did every four seconds.
 */
const GENERATING_POLL_MS = 4000;

/**
 * The plan as one calendar, or **null** when generation was never started.
 *
 * <p>Read again every few seconds **only while the newest generation is running**, and left alone otherwise: the
 * agent writes the finished plan, and nothing here signals it. A failure to read is shown and not retried behind the
 * back of the person.
 */
export function useStrategyPlan() {
  return useQuery<StrategyPlan | null>({
    queryKey: strategyKeys.plan(),
    queryFn: fetchStrategyPlan,
    retry: false,
    refetchInterval: (query) =>
      query.state.data?.status === 'generating' ? GENERATING_POLL_MS : false,
  });
}

/** The days of the reference plan, or null when there is none. A failure never breaks the calendar: it is secondary. */
export function useReferencePlanDays() {
  return useQuery<ReferencePlanDays | null>({
    queryKey: referenceKeys.planDays(),
    queryFn: fetchReferencePlanDays,
    retry: false,
  });
}
